<template>
  <span class="date-picker-wrapper">
    <date-picker v-model="currentValue"
                 :locale="localeConfiguration"
                 :mode="mode"
                 :model-config="modelConfig"
                 :minute-increment="15"
                 :is24hr="!showTimeMeridian"
                 :min-date="minDate"
                 :max-date="maxDate"
                 :transition="transition"
                 :popover="{visibility: 'focus', transition}"
                 :is-required="isRequired">
      <template #default="{ inputValue, inputEvents }">
        <!-- See: https://vcalendar.io/datepicker.html#input -->
        <input :id="inputId"
               class="date-picker-input form-control" :class="inputClass"
               :name="inputName"
               :value="inputValue"
               v-on="inputEvents">
      </template>
    </date-picker>
  </span>
</template>

<script>
import { DatePicker } from 'v-calendar'
import { DateTime } from 'luxon'
import DatePickerMixin from './date-picker-mixin'

// recommended by https://vcalendar.io/getting-started/installation.html
import 'v-calendar/style.css'

export default {
  components: { DatePicker },

  mixins: [ DatePickerMixin ],

  props: {
    mode: {
      type: String,
      required: false,
      default: 'dateTime'
    },
    initialValue: {
      type: Object, // a luxon DateTime object
      required: false
    },
    inputId: {
      type: String,
      required: false
    },
    inputClass: {
      type: String,
      required: false
    },
    inputName: {
      type: String,
      required: false,
      default: 'date'
    },
    disableFuture: {
      type: Boolean,
      required: false
    },
    disablePast: {
      type: Boolean,
      required: false
    },
    timeZone: {
      type: String,
      required: true
    },
    showTimeMeridian: {
      type: Boolean,
      required: false,
      default: false
    },
    isRequired: {
      type: Boolean,
      required: false,
      default: true
    }
  },

  // This component emits:
  // 'change' any time the value is updated, which may be caused by either user action or an updated prop
  // 'change-by-user' only when the value is updated by the user selecting a new date/time
  // This can be used to determine whether the user has overridden a default date/time setting.
  emits: ['change', 'change-by-user'],

  data() {
    const dataObject = {
      value: this.initialValue,
      modelConfig: {},
      transition: this.agraConfiguration.disableAnimations ? 'none' : 'focus'
    }

    // If we're in date-only mode, set the time to midnight when the date is selected.
    // Otherwise, the time would default to the current time, but not be shown in the UI since we're
    // in date-only mode.
    const timeAdjust = this.mode === 'date' ? '00:00:00' : null

    if (timeAdjust) {
      dataObject.modelConfig.timeAdjust = timeAdjust
    }
    return dataObject
  },

  computed: {
    currentValue: {
      get() {
        if (this.value) {
          // Convert from luxon DateTime object to JavaScript Date object
          return new Date(this.value.year, this.value.month - 1, this.value.day, this.value.hour, this.value.minute)
        }

        return null
      },

      set(value) {
        let newValue = null
        if (value) {
          // Convert from JavaScript Date object to luxon DateTime object
          newValue = DateTime.fromObject({ year: value.getFullYear(), month: value.getMonth() + 1, day: value.getDate(),
                                           hour: value.getHours(), minute: value.getMinutes() },
                                         { zone: this.timeZone })
        }

        // If the new value is not the same as the initialValue prop,
        // we can assume that this change was caused by user action.
        if (newValue.ts !== this.initialValue.ts) {
          this.$emit('change-by-user', newValue)
        }

        this.$emit('change', newValue)
      }
    },

    maxDate() {
      if (this.disableFuture) {
        return new Date().setHours(0, 0, 0, 0)
      }
      return null
    },

    minDate() {
      if (this.disablePast) {
        return new Date().setHours(0, 0, 0, 0)
      }
      return null
    }
  },

  watch: {
    initialValue() {
      this.value = this.initialValue
    }
  }
}
</script>

<style lang="scss" scoped>
  @use "~mixins/rtl_only" as rtl_override;

  .date-picker-wrapper {
    // In RTL: flip the navigation arrows vertically
    ::v-deep(.vc-arrows-container)  {
      .vc-arrow.vc-prev, .vc-arrow.vc-next {
        svg {
          @include rtl_override.rtl-only {
            transform: rotate(180deg);
          }
        }
      }
    }

    // Fix month name and prev/next button background color
    ::v-deep(.vc-header button) {
      background: transparent;
    }
  }
</style>
